import React, { useState, useRef, ChangeEvent } from 'react';
import { upload } from '../../assets';

export interface ImageUploadProps {
  id: string;
  label: string;
  value: File | null;
  previewUrl?: string | null;
  onChange: (file: File | null) => void;
  error?: string;
  accept?: string;
  maxWidth?: number;
  maxHeight?: number;
  allowDownload?: boolean;
  helpText?: string;
  className?: string;
  compact?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  id,
  label,
  value,
  previewUrl,
  onChange,
  error,
  accept = "image/png, image/jpeg",
  maxWidth = 512,
  maxHeight = 512,
  allowDownload = true,
  helpText = "Upload PNG or JPG",
  className = "",
  compact = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  const handleAreaClick = () => {
    fileInputRef.current?.click();
  };

  const validateFileType = (file: File): boolean => {
    const acceptedTypes = accept.split(',').map(type => type.trim());
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        // Handle extension-based validation
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else {
        // Handle MIME type validation
        return file.type === type;
      }
    });
  };

  const validateFileSize = (file: File): boolean => {
    // Default to 5MB max file size
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    return file.size <= MAX_FILE_SIZE;
  };

  const validateImageDimensions = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(
          (maxWidth === 0 || img.width <= maxWidth) && 
          (maxHeight === 0 || img.height <= maxHeight)
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const processFile = async (file: File | null) => {
    if (!file) {
      setUploadError(null);
      setLocalPreviewUrl(null);
      onChange(null);
      return;
    }

    // Clear previous errors
    setUploadError(null);

    // Validate file type
    if (!validateFileType(file)) {
      setUploadError(`File type not accepted. Please upload ${accept}`);
      return;
    }

    // Validate file size
    if (!validateFileSize(file)) {
      setUploadError("File size too large (max 5MB)");
      return;
    }

    // Validate image dimensions if it's an image
    if (file.type.startsWith('image/')) {
      const dimensionsValid = await validateImageDimensions(file);
      if (!dimensionsValid) {
        setUploadError(`Image dimensions exceed maximum (${maxWidth}x${maxHeight}px)`);
        return;
      }
    }

    // Create a preview URL
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setLocalPreviewUrl(url);
    } else {
      setLocalPreviewUrl(null);
    }

    // Notify parent component
    onChange(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    processFile(file);
  };

  const handleDelete = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
    setUploadError(null);
    onChange(null);
  };

  const handleDownload = () => {
    if (value) {
      const url = localPreviewUrl || (previewUrl || '');
      const a = document.createElement('a');
      a.href = url;
      a.download = value.name;
      a.click();
    }
  };

  // Determine the display preview URL (prefer local preview over passed-in preview)
  const displayPreviewUrl = localPreviewUrl || previewUrl;

  return (
    <div className={`flex flex-col ${className}`}>
      <label htmlFor={id} className="text-sm font-medium text-black mb-1.5">
        {label}
      </label>
      
      {/* Upload area */}
      <div
        className={`
          flex flex-col justify-center items-center 
          px-3 py-6 w-full 
          ${compact ? 'h-[126px]' : 'min-h-[180px]'}
          bg-white rounded-md 
          border ${isDragging ? 'border-[#6741c6] border-2' : error || uploadError ? 'border-red-500' : 'border-[#CBD5E1]'}
          cursor-pointer hover:bg-gray-50 transition-colors duration-200
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleAreaClick}
        data-testid={`${id}-upload-area`}
      >
        <input
          id={id}
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
          aria-label={label}
          data-testid={`${id}-input`}
        />
        
        <div className="flex flex-col items-center max-w-full">
          {displayPreviewUrl ? (
            <img
              src={displayPreviewUrl}
              alt={value?.name || "Uploaded preview"}
              className={`object-contain ${compact ? 'max-h-[80px]' : 'max-h-[120px]'} max-w-full rounded-sm`}
              data-testid={`${id}-preview`}
            />
          ) : (
            <img
              src={upload}
              className="object-contain w-6 aspect-square"
              alt="Upload icon"
            />
          )}
          
          {!displayPreviewUrl && (
            <>
              <div className="flex gap-1 justify-center items-center w-full text-base font-medium text-slate-900 mt-2 whitespace-nowrap">
                <span>Drag {compact ? '' : 'image here'} or browse</span>
              </div>
              <p className="text-xs text-center text-[#334155] mt-1">
                {helpText}
                {(maxWidth > 0 && maxHeight > 0) ? ` (max. ${maxWidth}x${maxHeight}px)` : ''}
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* Error message */}
      {(error || uploadError) && (
        <p className="text-red-500 text-xs mt-1" data-testid={`${id}-error`}>
          {error || uploadError}
        </p>
      )}
      
      {/* File info and actions */}
      {value && (
        <div className="flex flex-col justify-center p-4 mt-2 max-w-full bg-white rounded-md border border-solid border-slate-300">
          <div className="flex gap-10 justify-between items-center w-full max-md:max-w-full">
            <span className="self-stretch my-auto text-sm leading-none text-slate-900 truncate max-w-[70%]">
              {value.name}
            </span>
            <div className="flex gap-2 items-center self-stretch my-auto">
              {allowDownload && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square cursor-pointer text-slate-600 hover:text-slate-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  data-testid={`${id}-download`}
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square cursor-pointer text-red-500 hover:text-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                data-testid={`${id}-delete`}
              >
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;